"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronUp, CreditCard, ExternalLink, LogOut } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { ProductSwitcher } from "@/components/product-switcher";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
import type { Organization } from "@/lib/types";
interface CanvasSidebarProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  className?: string;
  onNewCanvas?: () => void;
}
export function CanvasSidebar(t0) {
  const $ = _c(73);
  const {
    organizations,
    selectedOrganization,
    onSelectOrganization,
    className,
    onNewCanvas
  } = t0;
  let switcherOrganizations;
  let t1;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp);
    t1 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t1;
  } else {
    switcherOrganizations = $[2];
    t1 = $[3];
  }
  const switcherSelectedOrganization = t1;
  const router = useRouter();
  const posthog = usePostHog();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t2;
  if ($[4] !== posthog || $[5] !== router || $[6] !== signOut) {
    t2 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://lounge.llmgateway.io/login");
          }
        }
      });
    };
    $[4] = posthog;
    $[5] = router;
    $[6] = signOut;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const logout = t2;
  const isAuthenticated = !!user;
  if (isUserLoading) {
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><div className="flex w-full items-center gap-2"><ProductSwitcher /><Badge>Canvas</Badge></div></div><StudioNav /></SidebarHeader>;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== className) {
      t4 = <Sidebar className={className}>{t3}</Sidebar>;
      $[9] = className;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  if (!isAuthenticated) {
    let t3;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex w-full items-center gap-2"><ProductSwitcher /><Badge>Canvas</Badge></div>;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t4;
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="font-medium mb-2">Sign in required</div>;
      t5 = <p className="text-muted-foreground mb-3">Please sign in to use Canvas.</p>;
      $[12] = t4;
      $[13] = t5;
    } else {
      t4 = $[12];
      t5 = $[13];
    }
    let t6;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t3}<div className="w-full rounded-md border p-4 text-sm">{t4}{t5}<div className="flex items-center justify-end gap-2">{t6}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== className) {
      t8 = <Sidebar className={className}>{t7}</Sidebar>;
      $[16] = className;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  const t3 = (className ?? "") + " max-md:hidden";
  let t4;
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SidebarMenuItem><ProductSwitcher /></SidebarMenuItem>;
    t5 = <SidebarChatSearch disabled={true} />;
    $[18] = t4;
    $[19] = t5;
  } else {
    t4 = $[18];
    t5 = $[19];
  }
  let t6;
  if ($[20] !== onNewCanvas) {
    t6 = <SidebarMenu>{t4}{t5}<SidebarNewAction label="New Canvas" onAction={onNewCanvas} /></SidebarMenu>;
    $[20] = onNewCanvas;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <StudioNav />;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t6) {
    t8 = <SidebarHeader>{t6}{t7}</SidebarHeader>;
    $[23] = t6;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== onSelectOrganization || $[26] !== switcherOrganizations || $[27] !== switcherSelectedOrganization) {
    t9 = switcherOrganizations.length > 0 ? <SidebarMenu className="group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[25] = onSelectOrganization;
    $[26] = switcherOrganizations;
    $[27] = switcherSelectedOrganization;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <SidebarContent className="px-2 py-4">{t9}</SidebarContent>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SidebarLoungePoints />;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const t12 = switcherSelectedOrganization ?? organization;
  const t13 = !switcherSelectedOrganization;
  let t14;
  if ($[32] !== isOrgLoading || $[33] !== t12 || $[34] !== t13) {
    t14 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t12} isLoading={isOrgLoading} isChatPlanOrg={t13} /></div>;
    $[32] = isOrgLoading;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = user?.name ?? "User";
  let t16;
  if ($[36] !== user?.name) {
    t16 = user?.name?.split(" ").map(_temp2).join("").toUpperCase().slice(0, 2) ?? "U";
    $[36] = user?.name;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16) {
    t17 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t16}</span></div>;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const t18 = user?.name;
  let t19;
  if ($[40] !== t18) {
    t19 = <span className="truncate font-semibold">{t18}</span>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const t20 = user?.email;
  let t21;
  if ($[42] !== t20) {
    t21 = <span className="truncate text-xs text-muted-foreground">{t20}</span>;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t19 || $[45] !== t21) {
    t22 = <div className="grid flex-1 text-left text-sm leading-tight">{t19}{t21}</div>;
    $[44] = t19;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t15 || $[49] !== t17 || $[50] !== t22) {
    t24 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t15} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t17}{t22}{t23}</SidebarMenuButton></DropdownMenuTrigger>;
    $[48] = t15;
    $[49] = t17;
    $[50] = t22;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  let t26;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t26 = <DropdownMenuSeparator />;
    $[52] = t25;
    $[53] = t26;
  } else {
    t25 = $[52];
    t26 = $[53];
  }
  let t27;
  let t28;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t28 = <DropdownMenuSeparator />;
    $[54] = t27;
    $[55] = t28;
  } else {
    t27 = $[54];
    t28 = $[55];
  }
  let t29;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span>Theme</span>;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  let t31;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp3}>{t29}<div onClick={_temp4} onKeyDown={_temp5}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t31 = <DropdownMenuSeparator />;
    $[57] = t30;
    $[58] = t31;
  } else {
    t30 = $[57];
    t31 = $[58];
  }
  let t32;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <LogOut className="mr-2 h-4 w-4" />;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== logout) {
    t33 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t25}{t26}{t27}{t28}{t30}{t31}<DropdownMenuItem onClick={logout}>{t32}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[60] = logout;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== t24 || $[63] !== t33) {
    t34 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t24}{t33}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[62] = t24;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== t14 || $[66] !== t34) {
    t35 = <SidebarFooter>{t11}{t14}{t34}</SidebarFooter>;
    $[65] = t14;
    $[66] = t34;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== t10 || $[69] !== t3 || $[70] !== t35 || $[71] !== t8) {
    t36 = <Sidebar collapsible="icon" className={t3}>{t8}{t10}{t35}</Sidebar>;
    $[68] = t10;
    $[69] = t3;
    $[70] = t35;
    $[71] = t8;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  return t36;
}
function _temp5(event_1) {
  return event_1.stopPropagation();
}
function _temp4(event_0) {
  return event_0.stopPropagation();
}
function _temp3(event) {
  return event.preventDefault();
}
function _temp2(n) {
  return n[0];
}
function _temp(org) {
  return org.kind === "default";
}